import { useCallback, useEffect } from "react";
import { User, CreateUser, UpdateUser } from "../types/models/User.d";
import { UpdateEntityUser } from "../types/models/User";
import { useGetUserListQuery, useInsertUserMutation, useUpdateUserMutation, useDeleteUserMutation, } from "../apis/User.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useUser(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertUserMutation, { 
        isLoading: insertUserLoading, 
        isSuccess: insertUserIsSuccess,
        isError: insertUserIsError,
        error: insertUserError,
    }] = useInsertUserMutation()
    const [updateUserMutation, { 
        isLoading: updateUserLoading,
        isSuccess: updateUserIsSuccess,
        isError: updateUserIsError,
        error: updateUserError,
    }] = useUpdateUserMutation()
    const [deleteUserMutation, { 
        isLoading: deleteUserLoading,
        isSuccess: deleteUserIsSuccess,
        isError: deleteUserIsError,
        error: deleteUserError,
    }] = useDeleteUserMutation()

    const loading =
        insertUserLoading ||
        updateUserLoading ||
        deleteUserLoading ||
        false

    const insertUser = useCallback(async (user: CreateUser, redirectPath: string|undefined) => {
        try {
            const data = await insertUserMutation(user).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertUserMutation, navigate])
    
    useEffect(() => {
        if (insertUserIsSuccess) {
            openSuccessAlert('User created')
        } else if (insertUserIsError) {
            console.log("add error", insertUserError)
            openFailAlert(t`User add error: ` + insertUserError?.data?.message)
        }
    }, [
        insertUserIsSuccess,
        insertUserIsError,
        insertUserError
    ]);

    const updateUser = useCallback(async (user: UpdateEntityUser, redirectPath: string|undefined) => {
        try {
            const data = await updateUserMutation(user).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateUserMutation, navigate])
    
    useEffect(() => {
        if (updateUserIsSuccess) {
            openSuccessAlert(t`User updated`)
        } else if (updateUserIsError) {
            console.log("update error", updateUserError)
            openFailAlert(t`User update error: ` + updateUserError?.data?.message)
        }
    }, [
        updateUserIsSuccess,
        updateUserIsError,
        updateUserError
    ]);

    const deleteUser = useCallback((user: User, redirectPath: string|undefined) => {
        openDialog(async () => {
            user = {
                ...user,
            }
            try {
                const { data } = await deleteUserMutation(user);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteUserMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteUserIsSuccess) {
            openSuccessAlert(t`User deleted`)
        } else if (deleteUserIsError) {
            console.log("delete error", deleteUserError)
            openFailAlert(t`User delete error: ` + deleteUserError?.data?.message)
        }
    }, [
        deleteUserIsSuccess,
        deleteUserIsError,
        deleteUserError
    ]);

    return {
        loading,
        insertUser,
        updateUser,
        deleteUser
    }
}
